import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, RotateCcw, Check } from "lucide-react";
import { Eyebrow, MaskLines, Reveal } from "./Motion";
import { FINDER_QUESTIONS, scoreFinder, saveFinderProfile } from "../../content/finder";
import { SOLUTIONS } from "../../content/solutions";

const Question = ({ step, answers, onPick }) => {
  const q = FINDER_QUESTIONS[step];
  return (
    <motion.div key={q.id} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
      <p className="font-mono text-xs text-[#74DC26]">Question {step + 1} of {FINDER_QUESTIONS.length}</p>
      <h3 className="font-display mt-4 text-2xl md:text-4xl font-medium leading-tight text-[#F9F8F3]" data-testid="finder-question">{q.q}</h3>
      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {q.options.map((o) => {
          const active = answers[q.id] === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onPick(q.id, o.v)}
              data-testid={`finder-option-${q.id}-${o.v}`}
              className={`group flex items-center justify-between gap-4 rounded-xl border px-5 py-5 text-left text-sm md:text-base font-medium transition-colors duration-300 ${
                active ? "border-[#74DC26] bg-[#74DC26] text-[#082619]" : "border-white/15 bg-white/5 text-[#F9F8F3] hover:border-[#74DC26]"
              }`}
            >
              {o.l}
              <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full border ${active ? "border-[#082619]" : "border-white/30 group-hover:border-[#74DC26]"}`}>
                {active && <Check className="h-3.5 w-3.5" />}
              </span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

const Results = ({ results, onRestart }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} data-testid="finder-results">
    <p className="font-mono text-xs text-[#74DC26]">Your indicative shortlist</p>
    <h3 className="font-display mt-4 text-2xl md:text-4xl font-medium text-[#F9F8F3]">Solutions worth discussing</h3>
    {results.length === 0 ? (
      <p className="mt-6 text-[#B4C7BA]" data-testid="finder-no-results">Your answers don't point clearly to one model. A short conversation is the best next step.</p>
    ) : (
      <div className="mt-8 grid gap-4 lg:grid-cols-3">
        {results.map((s, i) => (
          <div key={s.id} className={`flex flex-col rounded-2xl p-6 ${i === 0 ? "bg-[#74DC26] text-[#082619]" : "bg-[#0c3322] text-[#F9F8F3]"}`} data-testid={`finder-result-${s.id}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs opacity-80">{i === 0 ? "Strongest fit" : `Option ${i + 1}`}</span>
              <span className="font-mono text-xs opacity-80">{s.n}</span>
            </div>
            <h4 className="font-display mt-6 text-2xl font-medium">{s.title}</h4>
            <ul className="mt-4 flex-1 space-y-2">
              {s.reasons.slice(0, 3).map((r) => (
                <li key={r} className={`flex gap-2 text-sm leading-relaxed ${i === 0 ? "text-[#082619]/85" : "text-[#B4C7BA]"}`}>
                  <Check className="mt-1 h-3.5 w-3.5 shrink-0" />{r}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-4 text-sm font-semibold">
              <Link to={`/solutions#${s.id}`} className="link-underline" data-testid={`finder-read-${s.id}`}>Read more</Link>
              <Link to={`/contact?interest=${encodeURIComponent(s.title)}`} className="inline-flex items-center gap-1 link-underline" data-testid={`finder-discuss-${s.id}`}>
                Discuss this <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    )}
    <p className="mt-8 max-w-3xl text-xs leading-relaxed text-[#B4C7BA]">
      Your answers will be attached to your enquiry if you get in touch, so our team understands your facility before the first call.
    </p>
    <p className="mt-3 max-w-3xl text-xs leading-relaxed text-[#B4C7BA]">
      This shortlist is indicative and based only on your answers. Actual suitability, eligibility and economics depend on a site-specific evaluation, applicable regulations and location.
    </p>
    <div className="mt-8 flex flex-wrap gap-3">
      <Link to={`/contact?interest=${encodeURIComponent(results[0]?.title ?? "Not sure yet")}`} className="inline-flex items-center rounded-full bg-[#74DC26] px-6 py-3 text-sm font-semibold text-[#082619] transition-colors hover:bg-white" data-testid="finder-enquire">
        Request an Assessment
      </Link>
      <button type="button" onClick={onRestart} className="inline-flex items-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-semibold text-[#F9F8F3] hover:bg-white/10" data-testid="finder-restart">
        <RotateCcw className="h-4 w-4" /> Start again
      </button>
    </div>
  </motion.div>
);

export const SolutionFinder = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= FINDER_QUESTIONS.length;
  const results = done ? scoreFinder(answers, SOLUTIONS) : [];

  useEffect(() => {
    if (done) saveFinderProfile(answers, scoreFinder(answers, SOLUTIONS));
  }, [done, answers]);

  const onPick = (id, v) => {
    setAnswers((a) => ({ ...a, [id]: v }));
    setTimeout(() => setStep((s) => s + 1), 260);
  };
  const restart = () => { setAnswers({}); setStep(0); };

  return (
    <section id="finder" className="relative scroll-mt-24 bg-[#082619] py-24 md:py-32 grain" data-testid="solution-finder">
      <div className="relative z-10 mx-auto max-w-[1440px] px-5 md:px-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal><Eyebrow tone="light">Solution Finder</Eyebrow></Reveal>
            <h2 className="font-display mt-6 text-3xl sm:text-4xl lg:text-5xl font-medium leading-[1.05] tracking-tight text-[#F9F8F3]">
              <MaskLines inView lines={["Which solutions", <span className="font-serif-i text-[#74DC26]">fit your facility?</span>]} />
            </h2>
            <Reveal delay={0.15}>
              <p className="mt-6 text-base leading-relaxed text-[#B4C7BA]">Answer five quick questions to see which models are worth discussing. It takes under a minute.</p>
            </Reveal>
            <div className="mt-10 flex gap-1.5" aria-hidden="true">
              {FINDER_QUESTIONS.map((q, i) => (
                <span key={q.id} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i < step ? "bg-[#74DC26]" : "bg-white/15"}`} />
              ))}
            </div>
          </div>
          <div className="min-h-[340px] lg:col-span-8">
            {done ? <Results results={results} onRestart={restart} /> : <Question step={step} answers={answers} onPick={onPick} />}
            {!done && step > 0 && (
              <button type="button" onClick={() => setStep((s) => s - 1)} className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#B4C7BA] hover:text-white" data-testid="finder-back">
                <ArrowLeft className="h-4 w-4" /> Back
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
